import React from "react";
import { Link } from "react-router-dom";
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
  List,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserSecret,
  faCog,
  faChevronDown,
  faChevronUp,
  faUsers,
  faUserTie,
  faChalkboardTeacher,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../../config/AuthContext";
import routeToPermission from "../../../config/routeToPermission";

const TechPrepAdminLinks = () => {
  const [openSettings, setOpenSettings] = React.useState(false);
  const [openUsers, setOpenUsers] = React.useState(false);
  const theme = useTheme();
  const { userPermissions } = useAuth();

  const handleSettingsToggle = () => {
    setOpenSettings((prev) => !prev);
    setOpenUsers(false);
  };

  const handleUsersToggle = () => {
    setOpenUsers((prev) => !prev);
    setOpenSettings(false);
  };

  // Helper to check permission
  const canView = (areaKey) => {
    const permissionName = routeToPermission[areaKey];
    return permissionName ? userPermissions[permissionName]?.view : false;
  };

  console.log("DEBUG userPermissions:", userPermissions);
  console.log(
    "DEBUG canView Course Catalog:",
    canView("TpPortalMgtCourseCatalog"),
  );

  // Collect all settings links with their display names and render condition
  const settingsLinks = [
    canView("TpPortalMgtCourseCatalog") && {
      name: "Course Catalog",
      to: "/TpPortalMgtCourseCatalog",
    },
    canView("TpPortalMgtCourseShell") && {
      name: "Course Shell",
      to: "/TpPortalMgtCourseShell",
    },
    canView("TpCourseShellStatus") && {
      name: "Course Shell Status",
      to: "/TpCourseShellStatus",
    },
    canView("TpCreditCapture") && {
      name: "Credit Capture",
      to: "/TpCreditCapture",
    },
    canView("TpCreditCaptureStatus") && {
      name: "Credit Capture Status",
      to: "/TpCreditCaptureStatus",
    },
    canView("TpCreditTypes") && { name: "Credit Types", to: "/TpCreditTypes" },
    canView("TpCtag") && { name: "Ctag", to: "/TpCtag" },
    canView("TpDeliveryDetails") && {
      name: "Delivery Details",
      to: "/TpDeliveryDetails",
    },
    canView("TpDivisions") && { name: "Divisions", to: "/TpDivisions" },
    canView("TpElearnShellTerm") && {
      name: "Elearn Shell Term",
      to: "/TpElearnShellTerm",
    },
    canView("TpElearnShellType") && {
      name: "Elearn Shell Type",
      to: "/TpElearnShellType",
    },
    canView("TpFps") && { name: "FPS", to: "/TpFps" },
    canView("TpFpsCourses") && { name: "FPS Courses", to: "/TpFpsCourses" },
    canView("TpHeiPrograms") && { name: "HEI Programs", to: "/TpHeiPrograms" },
    canView("TpPartners") && { name: "Partners", to: "/TpPartners" },
    canView("TpPathwayPrograms") && {
      name: "Pathway Programs",
      to: "/TpPathwayPrograms",
    },
    canView("ProgramDelivery") && {
      name: "Program Delivery",
      to: "/ProgramDelivery",
    },
    canView("TpPTPForms") && { name: "PTP Forms", to: "/TpPTPForms" },
    canView("PTPStatusNote") && {
      name: "PTP Status Note",
      to: "/PTPStatusNote",
    },
    canView("TpResidenceStatusCodes") && {
      name: "Residence Status Codes",
      to: "/TpResidenceStatusCodes",
    },
    canView("TpShellStatus") && { name: "Shell Status", to: "/TpShellStatus" },
    canView("Showcase") && { name: "Showcase", to: "/tp-showcase" },
    canView("ShowcaseSubmissions") && {
      name: "Showcase Submissions",
      to: "/TpShowcaseSubmissions",
    },
    canView("TpSites") && { name: "Sites", to: "/TpSites" },
    canView("TpSsnStatusCode") && {
      name: "SSN Status Code",
      to: "/TpSsnStatusCode",
    },
    canView("TpStudents") && { name: "Students", to: "/TpStudents" },
  ].filter(Boolean);

  // Sort links alphabetically by display name
  settingsLinks.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {/* Users Menu */}
      {canView("TpUsers") && (
        <>
          <ListItem sx={{ mt: 2, color: theme.palette.text.primary }}>
            <ListItemIcon>
              <FontAwesomeIcon
                icon={faUserSecret}
                style={{ color: "red", fontSize: "2rem" }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Users"
              sx={{ color: theme.palette.text.primary }}
            />
            <IconButton onClick={handleUsersToggle} size="small">
              <FontAwesomeIcon
                icon={openUsers ? faChevronUp : faChevronDown}
                style={{ color: theme.palette.text.primary }}
              />
            </IconButton>
          </ListItem>
          <Collapse in={openUsers} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem sx={{ pl: 4, color: theme.palette.text.primary }}>
                <Link to="/TpUsers">
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={faUsers}
                      style={{ color: "#9c27b0", fontSize: "1.5rem" }}
                    />
                  </ListItemIcon>
                </Link>
                <Link to="/TpUsers">
                  <ListItemText
                    primary="Dashboard"
                    sx={{ color: theme.palette.text.primary }}
                  />
                </Link>
              </ListItem>
              <ListItem sx={{ pl: 4, color: theme.palette.text.primary }}>
                <Link to="/TpAdmins">
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={faUserSecret}
                      style={{ color: "#f44336", fontSize: "1.5rem" }}
                    />
                  </ListItemIcon>
                </Link>
                <Link to="/TpAdmins">
                  <ListItemText
                    primary="Admins"
                    sx={{ color: theme.palette.text.primary }}
                  />
                </Link>
              </ListItem>
              <ListItem sx={{ pl: 4, color: theme.palette.text.primary }}>
                <Link to="/TpCoordinators">
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={faUserTie}
                      style={{ color: "#2196f3", fontSize: "1.5rem" }}
                    />
                  </ListItemIcon>
                </Link>
                <Link to="/TpCoordinators">
                  <ListItemText
                    primary="Coordinators"
                    sx={{ color: theme.palette.text.primary }}
                  />
                </Link>
              </ListItem>
              <ListItem sx={{ pl: 4, color: theme.palette.text.primary }}>
                <Link to="/TpInstructors">
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={faChalkboardTeacher}
                      style={{ color: "#4caf50", fontSize: "1.5rem" }}
                    />
                  </ListItemIcon>
                </Link>
                <Link to="/TpInstructors">
                  <ListItemText
                    primary="Instructors"
                    sx={{ color: theme.palette.text.primary }}
                  />
                </Link>
              </ListItem>
              <ListItem sx={{ pl: 4, color: theme.palette.text.primary }}>
                <Link to="/TpStaff">
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={faUsers}
                      style={{ color: "#ff9800", fontSize: "1.5rem" }}
                    />
                  </ListItemIcon>
                </Link>
                <Link to="/TpStaff">
                  <ListItemText
                    primary="Staff"
                    sx={{ color: theme.palette.text.primary }}
                  />
                </Link>
              </ListItem>
            </List>
          </Collapse>
        </>
      )}
      {/* Data Link */}
      {canView("TpData") && (
        <ListItem sx={{ mt: 2, color: theme.palette.text.primary }}>
          <Link to="/TpData">
            <ListItemIcon>
              <FontAwesomeIcon
                icon={faChartLine}
                style={{ color: "#3f51b5", fontSize: "2rem" }}
              />
            </ListItemIcon>
          </Link>
          <Link to="/TpData">
            <ListItemText
              primary="Data"
              sx={{ color: theme.palette.text.primary }}
            />
          </Link>
        </ListItem>
      )}
      {/* School Users Link */}
      {canView("SchoolUsers") && (
        <ListItem sx={{ mt: 2, color: theme.palette.text.primary }}>
          <Link to="/SchoolUsers">
            <ListItemIcon>
              <FontAwesomeIcon
                icon={faUsers}
                style={{ color: "teal", fontSize: "2rem" }}
              />
            </ListItemIcon>
          </Link>
          <Link to="/SchoolUsers">
            <ListItemText
              primary="School Users"
              sx={{ color: theme.palette.text.primary }}
            />
          </Link>
        </ListItem>
      )}
      {/* Settings Section */}
      <ListItem
        sx={{
          justifyContent: "space-between",
          color: theme.palette.text.primary,
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={handleSettingsToggle}>
            <FontAwesomeIcon
              icon={faCog}
              style={{ fontSize: "2rem", color: "orange" }}
            />
          </IconButton>
          <ListItemText primary="App Settings" onClick={handleSettingsToggle} />
        </div>
        <IconButton onClick={handleSettingsToggle}>
          <FontAwesomeIcon
            icon={openSettings ? faChevronUp : faChevronDown}
            style={{ fontSize: "1rem", color: "black" }}
          />
        </IconButton>
      </ListItem>
      <Collapse in={openSettings} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {settingsLinks.map((link) => (
            <ListItem
              key={link.to}
              component={Link}
              to={link.to}
              onClick={handleSettingsToggle}
              sx={{ pl: 4, color: theme.palette.text.primary }}
            >
              <ListItemText primary={link.name} />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default TechPrepAdminLinks;
